import { BackgroundOperationState } from "../enums/background-operation-state.enum";
import { BackgroundOperationType } from "../enums/background-operation-type.enum";
import { BackgroundOperationStatusDto } from "../dtos/background-operation-status.dto";
import { RoutePlan } from "src/app/maps/route-plan";
import { UUID } from "angular2-uuid";

/**
 * A class representing a generic background operation to be displayed in the UI.
 * This interface can be extended for specific types of background operations or used by itself.
 */
export class BackgroundOperationViewModel {
	/**
	 * A client-generated unique identifier for the background operation, used to track pending operations before the server returns an ID.
	 */
	public ClientOperationId: string;

	/**
	 * The id returned from the api when the operation is created
	 */
	public BackgroundOperationId: string | null;

	/**
	 * A brief description of the operation for display purposes
	 */
	public Description: string;

	/**
	 * The current status of the operation
	 */
	public Status: BackgroundOperationState;

	/**
	 * The type of background operation.
	 */
	public Type: BackgroundOperationType;

	/** 
	 * Result data from processing (only populated on Success). 
	 */
	public ResultData: any;

	/** 
	 * Error message if the request failed (only populated on Failed).
	 */
	public ErrorMessage: string | null;

	/**
	 * Indicates if the operation is currently being polled for status updates.
	 */
	public IsPolling: boolean;

	/**
	 * Constructs a new BackgroundOperationViewModel instance.
	 * This flexible constructor allows for various initialization scenarios, such as creating a new operation with just a description and type, or mapping from a complete DTO response from the server.
	 * @param data 
	 * An object containing the initialization data for the background operation.
	 * This parameter uses an intersection type (&) combining:
	 * - Partial<BackgroundOperationStatusDto>: Any subset of properties from BackgroundOperationStatusDto (all optional)
	 * - { Description: string; Type: BackgroundOperationType }: Required Description and Type properties
	 * 
	 * This means you can pass any properties from BackgroundOperationStatusDto (like BackgroundOperationId, Status, ResultData, ErrorMessage),
	 * but only Description and Type are required. All other properties are optional.
	 * @example
	 * // Minimal usage with only required properties
	 * new BackgroundOperationViewModel({ Description: "Processing data", Type: BackgroundOperationType.EsriCalculateEtas });
	 * @example
	 * // With additional optional properties
	 * new BackgroundOperationViewModel({ 
	 *   Description: "Optimizing route", 
	 *   Type: BackgroundOperationType.EsriOptimizeRoute,
	 *   BackgroundOperationId: "12345",
	 *   Status: BackgroundOperationState.Processing
	 * });
	 * @example
	 * // Mapping from a DTO (e.g., in an RxJS pipe)
	 * this.backgroundOperationDataService.GetBackgroundOperationStatus$(operation).pipe(
	 *   map((result: BackgroundOperationStatusDto) => new BackgroundOperationViewModel(result))
	 * );
	 */
	public constructor(data: Partial<BackgroundOperationStatusDto> & { Description: string; Type: BackgroundOperationType }) {
		this.ClientOperationId = UUID.UUID();
		this.BackgroundOperationId = data.BackgroundOperationId || null;
		this.Description = data.Description;
		this.Status = data.Status || BackgroundOperationState.Pending;
		this.Type = data.Type;
		this.ResultData = this.MapResultData(data.Type, data.ResultData);
		this.ErrorMessage = data.ErrorMessage || null;
		this.IsPolling = false;
	}

	/**
	 * Indicates if the background operation is currently processing.
	 * @returns True if the operation status is pending, processing, or locked processing; otherwise, false.
	 * @readonly
	 */
	public get IsProcessing(): boolean {
		return this.Status === BackgroundOperationState.Pending || this.Status === BackgroundOperationState.Processing || this.Status === BackgroundOperationState.LockedProcessing;
	}

	/**
	 * Updates the properties of the BackgroundOperationViewModel instance based on the provided BackgroundOperationStatusDto.
	 * @param backgroundOperationDto The dto to update from
	 * @returns The updated instance of this BackgroundOperationViewModel.
	 */
	public UpdateFromDto(backgroundOperationDto: BackgroundOperationStatusDto): this {
		if (this.Type !== backgroundOperationDto.Type) {
			throw new Error(`Cannot update BackgroundOperationViewModel: Type mismatch (current: ${this.Type}, dto: ${backgroundOperationDto.Type})`);
		}

		this.BackgroundOperationId = backgroundOperationDto.BackgroundOperationId;
		this.Status = backgroundOperationDto.Status;
		this.ResultData = this.MapResultData(backgroundOperationDto.Type, backgroundOperationDto.ResultData);
		this.ErrorMessage = backgroundOperationDto.ErrorMessage;
		return this;
	}

	/** 
	 * Maps the result data of a BackgroundOperationDto to the appropriate type.
	 * @param operationType The type of background operation, used to determine how to map the result data.
	 * @param resultData The raw result data from the BackgroundOperationDto.
	 * @returns The mapped result data.
	 */
	private MapResultData(operationType: BackgroundOperationType, resultData: any): any {
		// If the operation is an Esri background operation, map the result data to a RoutePlan, it comes back as a RoutePlan[] with a single item, but we only need the first item.
		if ((operationType === BackgroundOperationType.EsriCalculateEtas || operationType === BackgroundOperationType.EsriOptimizeRoute) 
			&& resultData 
			&& Array.isArray(resultData)
			&& resultData.length > 0) {
			const routePlan: RoutePlan[] = resultData as RoutePlan[];
			return routePlan[0];
		}

		return resultData;
	}
}